<template>
  <section v-if="storeId && targetId" class="ou-assignment">
    <header class="ou-assignment__header">
      <div>
        <p class="ou-assignment__eyebrow">Operational assignment</p>
        <h3>{{ heading }}</h3>
        <p>
          {{
            correction
              ? editable
                ? "Correct finalized accounting attribution with a required audit reason."
                : "Finalized accounting attribution is read-only without store-settings privilege."
              : "Use this store's terminology to identify where this activity belongs."
          }}
        </p>
      </div>
      <hs-badge v-if="currentUnit" :tone="statusTone(currentStatus)">
        {{ currentStatusLabel }}
      </hs-badge>
    </header>

    <hs-alert v-if="error" tone="danger" :message="error" />

    <div v-if="loading" class="ou-assignment__loading">Loading available units…</div>
    <template v-else-if="types.length || units.length">
      <div class="ou-assignment__current">
        <span>{{ singularLabel }}</span>
        <strong>{{ currentUnit?.name || currentUnit?.code || "Not assigned" }}</strong>
        <small v-if="currentUnit">{{ currentUnit.code }}</small>
      </div>

      <div v-if="editable" class="ou-assignment__form">
        <hs-select
          v-model="selectedUnitId"
          :label="singularLabel"
          :options="unitOptions"
          :placeholder="`Select ${singularLabel.toLowerCase()}`"
          :disabled="saving"
        />
        <hs-input
          v-model="reason"
          label="Reason"
          :placeholder="correction ? 'Required accounting correction reason' : 'Optional assignment or transfer note'"
          :required="correction"
          :disabled="saving"
        />
        <hs-button
          variant="primary"
          icon="map-pin"
          :loading="saving"
          :disabled="saving || !hasChanged || (correction && !reason.trim())"
          @click="save"
        >
          {{ correction ? "Correct attribution" : currentUnit ? "Update assignment" : "Assign" }}
        </hs-button>
      </div>

      <div class="ou-assignment__footer">
        <button type="button" @click="toggleHistory">
          {{ showHistory ? "Hide" : "View" }} assignment history
        </button>
        <span>{{ availableCount }} available</span>
      </div>

      <div v-if="showHistory" class="ou-assignment__history">
        <p v-if="historyLoading">Loading history…</p>
        <p v-else-if="!history.length">No assignment changes recorded.</p>
        <article v-for="entry in history" :key="entry.id || entry.created_at">
          <strong>{{ historyDescription(entry) }}</strong>
          <span>{{ formatDate(entry.created_at || entry.changed_at) }}</span>
          <small v-if="entry.reason">{{ entry.reason }}</small>
        </article>
      </div>
    </template>
    <div v-else class="ou-assignment__empty">
      No operational units are configured for this store. Existing workflows continue without an
      assignment.
    </div>
  </section>
</template>

<script>
import { HsAlert, HsBadge, HsButton, HsInput, HsSelect } from "@/components/ui";
import {
  assignOperationalUnit,
  correctSaleCartOperationalUnit,
  getOperationalUnitAssignmentHistory,
  getOperationalUnitAvailability,
  getOperationalUnits,
  getOperationalUnitTypes,
  operationalUnitError,
} from "@/services/operationalUnitsService";

export default {
  name: "OperationalUnitAssignment",
  components: { HsAlert, HsBadge, HsButton, HsInput, HsSelect },
  props: {
    storeId: { type: [String, Number], required: true },
    targetId: { type: [String, Number], required: true },
    targetType: {
      type: String,
      required: true,
      validator: (value) => ["ticket", "sale-cart"].includes(value),
    },
    modelValue: { type: Object, default: null },
    editable: { type: Boolean, default: true },
    correction: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "assigned"],
  data() {
    return {
      types: [],
      units: [],
      availability: [],
      currentUnit: this.modelValue,
      selectedUnitId: this.modelValue?.id || "",
      reason: "",
      loading: false,
      saving: false,
      error: "",
      showHistory: false,
      historyLoading: false,
      history: [],
    };
  },
  computed: {
    currentAvailability() {
      return this.availability.find((entry) => this.availabilityUnitId(entry) == this.currentUnit?.id);
    },
    currentStatus() {
      return this.statusOf(this.currentAvailability) || (this.currentUnit?.out_of_service ? "unavailable" : "assigned");
    },
    currentStatusLabel() {
      return this.currentStatus.replaceAll("_", " ");
    },
    currentType() {
      if (this.currentUnit?.operational_unit_type) {
        return this.currentUnit.operational_unit_type;
      }
      const typeId = this.currentUnit?.operational_unit_type_id || this.currentUnit?.operational_unit_type?.id;
      return this.types.find((type) => type.id == typeId);
    },
    singularLabel() {
      return this.currentType?.singular_label || this.types[0]?.singular_label || "Operational unit";
    },
    heading() {
      if (this.correction) return `Correct finalized ${this.singularLabel.toLowerCase()}`;
      return this.currentUnit ? `${this.singularLabel} assignment` : "Assign operational unit";
    },
    availableCount() {
      return this.availability.filter((entry) => this.statusOf(entry) === "available").length;
    },
    unitOptions() {
      const options = [{ value: "", label: `No ${this.singularLabel.toLowerCase()}` }];
      this.types.forEach((type) => {
        this.units
          .filter((unit) => {
            const typeId = unit.operational_unit_type_id || unit.operational_unit_type?.id;
            return typeId == type.id && (this.correction || unit.active !== false);
          })
          .forEach((unit) => {
            const availability = this.availability.find(
              (entry) => this.availabilityUnitId(entry) == unit.id,
            );
            const status = this.statusOf(availability) || (unit.out_of_service ? "unavailable" : "available");
            const unavailable = ["occupied", "reserved", "unavailable"].includes(status);
            const isCurrent = unit.id == this.currentUnit?.id;
            if (this.correction || !unavailable || isCurrent) {
              options.push({
                value: unit.id,
                label: `${type.singular_label}: ${unit.name} (${unit.active === false ? "archived, " : ""}${status.replaceAll("_", " ")})`,
              });
            }
          });
      });
      return options;
    },
    hasChanged() {
      return String(this.selectedUnitId || "") !== String(this.currentUnit?.id || "");
    },
  },
  watch: {
    modelValue(value) {
      this.currentUnit = value;
      this.selectedUnitId = value?.id || "";
    },
    storeId: "load",
    targetId: "load",
  },
  mounted() {
    this.load();
  },
  methods: {
    availabilityUnitId(entry) {
      return entry?.operational_unit_id || entry?.unit_id || entry?.operational_unit?.id || entry?.unit?.id || entry?.id;
    },
    statusOf(entry) {
      return String(entry?.effective_availability || entry?.availability || entry?.status || "").toLowerCase();
    },
    statusTone(status) {
      return { available: "success", occupied: "warning", reserved: "primary", unavailable: "danger" }[status] || "neutral";
    },
    async load() {
      if (!this.storeId || !this.targetId) return;
      this.loading = true;
      this.error = "";
      try {
        [this.types, this.units, this.availability] = await Promise.all([
          getOperationalUnitTypes(this.storeId, {
            include_archived: this.correction,
          }),
          getOperationalUnits(this.storeId, {
            include_archived: this.correction,
          }),
          getOperationalUnitAvailability(this.storeId),
        ]);
        if (this.currentUnit?.id) {
          if (!this.currentUnit.snapshot) {
            this.currentUnit =
              this.units.find((unit) => unit.id == this.currentUnit.id) || this.currentUnit;
          }
        }
      } catch (error) {
        this.error = operationalUnitError(error, "Unable to load operational units.");
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (this.correction && !this.reason.trim()) {
        this.error = "A reason is required for finalized sale attribution corrections.";
        return;
      }
      this.saving = true;
      this.error = "";
      try {
        const payload = {
          operational_unit_id: this.selectedUnitId || null,
          reason: this.reason.trim() || undefined,
        };
        const result = this.correction
          ? await correctSaleCartOperationalUnit(this.storeId, this.targetId, payload)
          : await assignOperationalUnit(this.storeId, this.targetType, this.targetId, payload);
        const returnedUnit = result?.operational_unit;
        this.currentUnit =
          returnedUnit ||
          this.units.find((unit) => unit.id == this.selectedUnitId) ||
          null;
        this.selectedUnitId = this.currentUnit?.id || "";
        this.reason = "";
        this.$emit("update:modelValue", this.currentUnit);
        this.$emit("assigned", { operationalUnit: this.currentUnit, response: result });
        await this.load();
        if (this.showHistory) await this.loadHistory();
      } catch (error) {
        this.error = operationalUnitError(error, "Unable to update the assignment.");
      } finally {
        this.saving = false;
      }
    },
    async toggleHistory() {
      this.showHistory = !this.showHistory;
      if (this.showHistory && !this.history.length) await this.loadHistory();
    },
    async loadHistory() {
      this.historyLoading = true;
      try {
        this.history = await getOperationalUnitAssignmentHistory(
          this.storeId,
          this.targetType,
          this.targetId,
        );
      } catch (error) {
        this.error = operationalUnitError(error, "Unable to load assignment history.");
      } finally {
        this.historyLoading = false;
      }
    },
    historyDescription(entry) {
      const previous = entry.previous_operational_unit?.name || entry.previous_unit?.name || "Unassigned";
      const next = entry.operational_unit?.name || entry.new_operational_unit?.name || entry.new_unit?.name || "Unassigned";
      return `${previous} → ${next}`;
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : "";
    },
  },
};
</script>

<style scoped>
.ou-assignment {
  background: color-mix(in srgb, var(--hs-surface, #fff) 94%, var(--hs-primary, #3293ec));
  border: 1px solid var(--hs-border, #e2e8f0);
  border-radius: 18px;
  display: grid;
  gap: 1rem;
  padding: 1rem;
}
.ou-assignment__header { align-items: flex-start; display: flex; gap: 1rem; justify-content: space-between; }
.ou-assignment__header h3 { color: var(--hs-text, #0f172a); font-size: 1rem; font-weight: 900; margin: 0; }
.ou-assignment__header p, .ou-assignment__loading, .ou-assignment__empty { color: var(--hs-muted, #64748b); font-size: .82rem; margin: .25rem 0 0; }
.ou-assignment__eyebrow { color: var(--hs-primary, #3293ec) !important; font-size: .68rem !important; font-weight: 900; letter-spacing: .1em; text-transform: uppercase; }
.ou-assignment__current { background: var(--hs-bg, #f8fafc); border-radius: 12px; display: flex; flex-wrap: wrap; gap: .5rem; padding: .8rem; }
.ou-assignment__current span { color: var(--hs-muted, #64748b); font-size: .75rem; width: 100%; }
.ou-assignment__current strong { color: var(--hs-text, #0f172a); }
.ou-assignment__current small { color: var(--hs-muted, #64748b); }
.ou-assignment__form { align-items: end; display: grid; gap: .75rem; grid-template-columns: minmax(180px, 1fr) minmax(180px, 1fr) auto; }
.ou-assignment__footer { color: var(--hs-muted, #64748b); display: flex; font-size: .76rem; justify-content: space-between; }
.ou-assignment__footer button { color: var(--hs-primary, #3293ec); font-weight: 800; }
.ou-assignment__history { border-top: 1px solid var(--hs-border, #e2e8f0); display: grid; gap: .6rem; padding-top: .75rem; }
.ou-assignment__history article { display: grid; font-size: .78rem; gap: .15rem; }
.ou-assignment__history span, .ou-assignment__history small, .ou-assignment__history p { color: var(--hs-muted, #64748b); }
@media (max-width: 760px) { .ou-assignment__form { grid-template-columns: 1fr; } }
</style>
